import type { ReactNode } from "react";
import type { CartMap, Product, RecommendationItem } from "../../types";
import RecommendedProductCard from "./RecommendedProductCard";
import RecommendedProductListItem from "./RecommendedProductListItem";

interface QuantityChangeOptions {
  source?: "catalog" | "recommendation" | string;
}

interface ProductRecommendationsProps {
  items?: RecommendationItem[];
  isLoading?: boolean;
  cart?: CartMap;
  onQuantityChange?: (
    product: Product,
    value: number,
    options?: QuantityChangeOptions,
  ) => void;
  baseMinQuantity?: number;
  layout?: "list" | "grid";
  imageUrls?: Record<number, string>;
}

const ProductRecommendations = ({
  items = [],
  isLoading = false,
  cart = {},
  onQuantityChange,
  baseMinQuantity = 0,
  layout = "list",
  imageUrls = {},
}: ProductRecommendationsProps) => {
  const isGridLayout = layout === "grid";
  const handleRecommendedQuantityChange = (product: Product, value: number) => {
    if (typeof onQuantityChange !== "function") {
      return;
    }
    onQuantityChange(product, value, { source: "recommendation" });
  };

  if (isLoading) {
    return (
      <div className="product-recommendations">
        <p className="product-recommendations-title">Polecane</p>
        <p className="product-recommendations-status">Ladowanie rekomendacji...</p>
      </div>
    );
  }

  if (!Array.isArray(items) || items.length === 0) {
    return (
      <div className="product-recommendations">
        <p className="product-recommendations-title">Polecane</p>
        <p className="product-recommendations-status">
          Brak produktow polecanych dla tej pozycji.
        </p>
      </div>
    );
  }

  return (
    <div
      className={`product-recommendations${isGridLayout ? " product-recommendations-grid" : ""}`}
    >
      <p className="product-recommendations-title">Polecane</p>
      {isGridLayout ? (
        <div className="product-recommendations-list-grid">
          {items.map((item) => (
            <RecommendedProductCard
              key={`recommended-${item.id}`}
              item={item}
              cart={cart}
              onQuantityChange={handleRecommendedQuantityChange}
              baseMinQuantity={baseMinQuantity}
              imageUrls={imageUrls}
            />
          ))}
        </div>
      ) : (
        <ul className="product-recommendations-list">
          {items.map((item) => (
            <RecommendedProductListItem
              key={`recommended-${item.id}`}
              item={item}
              cart={cart}
              onQuantityChange={handleRecommendedQuantityChange}
              baseMinQuantity={baseMinQuantity}
            />
          ))}
        </ul>
      )}
    </div>
  );
};

export default ProductRecommendations;
